import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { AUTH } from '@brickup/shared/locales/de/auth'
import { COMMON } from '@brickup/shared/locales/de/common'
import { getSubscriptionStatus, resubscribe, unsubscribe } from '@/api/email'
import { TONE_INK } from '@/components/base'
import { CtaButton } from '@/components/CtaButton'
import {
  PublicPageCard,
  PublicPageCardLoading,
  PublicPageCardMessage,
  PublicPageHeading,
  PublicPageText,
} from '@/components/Public/PublicPageCard'
import { SnackbarType } from '@/components/Snackbar'
import { useMutationErrorSnackbar } from '@/hooks/use-mutation-error-snackbar'
import { useSnackbar } from '@/hooks/use-snackbar'

export const Route = createFileRoute('/public/unsubscribe/$token')({
  component: UnsubscribeScreen,
})

/** The link at the foot of every e-mail: what this address is set to, and the
 *  one button that flips it. */
function UnsubscribeScreen() {
  const { token } = Route.useParams()
  const addSnackbar = useSnackbar()
  const showError = useMutationErrorSnackbar()
  const queryClient = useQueryClient()

  const {
    data: status,
    isLoading,
    error: statusError,
  } = useQuery({
    queryKey: ['subscription-status', token],
    queryFn: () => getSubscriptionStatus(token),
    retry: false,
  })

  const flip = (
    request: (token: string) => Promise<unknown>,
    successTitle: string,
  ) => ({
    mutationFn: () => request(token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subscription-status', token],
      })
      addSnackbar({ title: successTitle, type: SnackbarType.SUCCESS })
    },
    onError: (error: unknown) => showError(error, COMMON.errors.networkError),
  })

  const unsubscribeMutation = useMutation(flip(unsubscribe, AUTH.unsubscribed))
  const resubscribeMutation = useMutation(flip(resubscribe, AUTH.resubscribed))

  const isPending =
    unsubscribeMutation.isPending || resubscribeMutation.isPending

  if (isLoading) return <PublicPageCardLoading />

  if (statusError || !status) {
    return (
      <PublicPageCardMessage
        title={AUTH.invalidToken}
        description={AUTH.invalidTokenDescription}
        variant="error"
      />
    )
  }

  const isSubscribed = status.isSubscribed

  return (
    <PublicPageCard>
      <PublicPageHeading
        title={isSubscribed ? AUTH.unsubscribeTitle : AUTH.resubscribeTitle}
        description={
          isSubscribed
            ? AUTH.unsubscribeDescription
            : AUTH.resubscribeDescription
        }
      />

      {status.email ? <PublicPageText>{status.email}</PublicPageText> : null}

      <div className="flex flex-row items-center justify-center gap-2 rounded-md bg-neutral-50 p-3">
        <span className="text-sm text-neutral-500">{AUTH.currentStatus}:</span>
        <span
          className={`text-sm font-semibold ${
            isSubscribed ? TONE_INK.success : TONE_INK.error
          }`}
        >
          {isSubscribed ? AUTH.subscribed : AUTH.notSubscribed}
        </span>
      </div>

      <CtaButton
        tone={isSubscribed ? 'outline' : 'solid'}
        label={isSubscribed ? AUTH.unsubscribeButton : AUTH.resubscribeButton}
        icon={null}
        layout="full"
        loading={isPending}
        onClick={() =>
          (isSubscribed ? unsubscribeMutation : resubscribeMutation).mutate()
        }
      />
    </PublicPageCard>
  )
}
